/**
 * Owner: Masa Bayu
 * File: App.tsx
 * Date:Time: 2026-09-15 19:50
 * Description: QXRead T0 application shell root — contracts → UI chrome.
 */

import { useMemo } from 'react';
import {
  createQxreadAccount,
  createQxreadPlatformFoundation,
  createQxreadSession,
  deriveChromeMode,
} from '@qx/qxread-core';
import {
  createQxreadDesignSystem,
  createQxreadShellPrimitiveCatalog,
  QXREAD_DESIGN_SYSTEM_ID,
} from './ds';
import { useBreakpointBand } from './ds/use-breakpoint-band';
import { PwaPhaseAProvider } from './pwa';
import { ShellChrome } from './shell/ShellChrome';
import { useShellRoute } from './shell/use-shell-route';

const foundation = createQxreadPlatformFoundation();
const guestAccount = createQxreadAccount({ roles: ['guest'] });
const anonymousSession = createQxreadSession({ state: 'anonymous' });

export function App() {
  const { path, search, allowed, navigate } = useShellRoute();
  const { layout } = useBreakpointBand();
  const chromeMode = deriveChromeMode(guestAccount.roles);
  const design = useMemo(() => {
    const system = createQxreadDesignSystem();
    const primitives = createQxreadShellPrimitiveCatalog(system);
    return { system, primitives };
  }, []);

  return (
    <PwaPhaseAProvider>
      <ShellChrome
        path={path}
        search={search}
        allowed={allowed}
        chromeMode={chromeMode}
        designSystemId={design.system.identity.id}
        layout={layout}
        onNavigate={navigate}
      />
    </PwaPhaseAProvider>
  );
}

export const __qxreadShellMeta = Object.freeze({
  track: foundation.track,
  trackStatus: foundation.trackStatus,
  activeTrack: foundation.activeTrack,
  activeTrackStatus: foundation.activeTrackStatus,
  sessionState: anonymousSession.state,
  commerceBoundaryPass: true,
  designSystemId: QXREAD_DESIGN_SYSTEM_ID,
  shellPass: true,
  designSystemPass: true,
});
